import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Maw — top-down katamari-style scale-shifter; consume objects of
 * mass ≤ your radius; grow to consume bigger ones; decay over time.
 * Game 17 of platform games + 6-game rebrand pack (MVP / v0).
 *
 * Design dossier: docs/dossier-games/17-maw/
 * Source IP: Katamari Damacy + Agar.io scale-progression canon.
 *
 * MVP scope:
 *   - 60×60 top-down arena with a deterministic cityscape from seed
 *     (200 objects, mass 0.2..8 biased toward small)
 *   - Maw circle player; arrows/WASD steer; integrates velocity
 *   - Radius grows on consume (r' = sqrt(r² + mass²)); decays
 *     exponentially over time
 *   - 20 Hz position trace + eat-event list for server replay
 *   - Score = floor(peak_radius × 100) + eat_count
 *   - 90 s run cap
 *
 * Deferred to v2 polish (NOT in MVP):
 *   - Spatial hash for O(1) neighbor queries (linear scan @ 200 = fine)
 *   - InstancedMesh pools per mass class
 *   - Tilt input on mobile
 *   - Camera follow-with-lookahead bias
 *   - Post-processing
 */

// ============================================================
// Constants — must stay byte-identical with scoring-maw.ts
// ============================================================

export const RUN_MS = 90_000;
export const ARENA_HALF = 30.0;
export const SAMPLE_INTERVAL_MS = 50;
export const MAX_SAMPLES = 1800;
export const MAX_EATS = 240;

export const MAW_START_RADIUS = 0.8;
export const MIN_MASS = 0.2;
export const MAX_MASS = 8.0;
export const MAX_SPEED = 14.0;
export const DECAY_RATE = 0.05;
/** v2(b) escalation: decay rate ramps linearly from DECAY_RATE at
 *  t=0 to DECAY_RATE_END at t=RUN_MS. Server uses closed-form integral
 *  for exact replay; this client class uses instantaneous decay per
 *  frame for the HUD estimate. */
export const DECAY_RATE_END = 0.10;
export const OBJECT_COUNT = 200;
export const HUNTER_FIRST_SPAWN_MS = 20_000;
export const HUNTER_SPAWN_INTERVAL_MS = 15_000;
export const HUNTER_SPEED = 8.0;
export const HUNTER_RADIUS = 0.5;
export const HUNTER_BITE_FACTOR = 0.75;

const STEER_ACCEL = 28.0;
const STEER_DAMP = 4.0;

// ============================================================
// Cityscape generator — byte-identical with scoring-maw
// ============================================================

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface CityObject { idx: number; x: number; y: number; mass: number }

export interface HunterSpawn { idx: number; spawn_t: number; x: number; y: number }

export function generateHunterSchedule(seed: number): HunterSpawn[] {
  const rng = makeRng(seed ^ 0x484e5452);
  const out: HunterSpawn[] = [];
  let t = HUNTER_FIRST_SPAWN_MS;
  let idx = 0;
  while (t < RUN_MS) {
    const edge = Math.floor(rng() * 4);
    const along = (rng() * 2 - 1) * (ARENA_HALF - 1);
    let x = 0, y = 0;
    if (edge === 0) { x = along; y = ARENA_HALF - 0.5; }
    else if (edge === 1) { x = ARENA_HALF - 0.5; y = along; }
    else if (edge === 2) { x = along; y = -ARENA_HALF + 0.5; }
    else { x = -ARENA_HALF + 0.5; y = along; }
    out.push({ idx, spawn_t: t, x, y });
    idx += 1;
    t += HUNTER_SPAWN_INTERVAL_MS;
  }
  return out;
}

export function generateCityscape(seed: number): CityObject[] {
  const rng = makeRng(seed ^ 0x4d415700);
  const out: CityObject[] = [];
  for (let i = 0; i < OBJECT_COUNT; i++) {
    const x = (rng() * 2 - 1) * (ARENA_HALF - 1);
    const y = (rng() * 2 - 1) * (ARENA_HALF - 1);
    const u = rng();
    const mass = MIN_MASS + (MAX_MASS - MIN_MASS) * Math.pow(u, 2.2);
    out.push({ idx: i, x, y, mass });
  }
  return out;
}

// ============================================================
// Types
// ============================================================

export interface PositionSample { t: number; x: number; y: number }
export interface EatEvent { t: number; object_idx: number }

export interface MawResult {
  peak_radius: number;
  eats: number;
  hunter_bites: number;
  duration_ms: number;
  samples: PositionSample[];
  eatEvents: EatEvent[];
  scoreEstimate: number;
}

export interface MawCallbacks {
  onHud: (runMs: number, radius: number, eats: number, scoreEstimate: number) => void;
  onGameOver: (result: MawResult) => void;
}

// ============================================================
// Game class
// ============================================================

const q01 = (v: number) => Math.round(v * 100) / 100;

export class Maw {
  private engine: Engine;
  private audio = new AudioBed();
  private objects: CityObject[];
  private objectMeshes: THREE.Mesh[] = [];
  private mawMesh!: THREE.Mesh;
  private mawGlow!: THREE.PointLight;

  // State
  private mx = 0;
  private my = 0;
  private mvx = 0;
  private mvy = 0;
  private radius = MAW_START_RADIUS;
  private peakRadius = MAW_START_RADIUS;
  private alive = new Uint8Array(OBJECT_COUNT).fill(1);
  private samples: PositionSample[] = [];
  private eats: EatEvent[] = [];
  private sampleTimer = 0;
  private startedAt = 0;
  private gameOver = false;
  private cb: MawCallbacks;

  // v5 hunters
  private hunterSchedule: HunterSpawn[] = [];
  private hunterSpawnIdx = 0;
  private hunters: { idx: number; x: number; y: number; alive: boolean; mesh: THREE.Mesh }[] = [];
  private hunterBites = 0;
  private hunterMat!: THREE.MeshStandardMaterial;
  private hunterGeo!: THREE.SphereGeometry;

  // Input
  private up = false;
  private down = false;
  private left = false;
  private right = false;
  // Mobile touch: continuous vec set by an external joystick. (x, y) in
  // [-1, 1]; y positive = down (matches Maw's screen-y-down convention,
  // so main-maw.ts flips the joystick's world-y-up output before passing).
  // When both components are zero, keyboard input drives steering.
  private touchVec = { x: 0, y: 0 };
  setTouchVec(x: number, y: number) { this.touchVec.x = x; this.touchVec.y = y; }

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "ArrowUp" || e.code === "KeyW") this.up = true;
    if (e.code === "ArrowDown" || e.code === "KeyS") this.down = true;
    if (e.code === "ArrowLeft" || e.code === "KeyA") this.left = true;
    if (e.code === "ArrowRight" || e.code === "KeyD") this.right = true;
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.code === "ArrowUp" || e.code === "KeyW") this.up = false;
    if (e.code === "ArrowDown" || e.code === "KeyS") this.down = false;
    if (e.code === "ArrowLeft" || e.code === "KeyA") this.left = false;
    if (e.code === "ArrowRight" || e.code === "KeyD") this.right = false;
  };

  constructor(canvas: HTMLCanvasElement, cb: MawCallbacks, seed: number) {
    this.cb = cb;
    this.objects = generateCityscape(seed);
    this.hunterSchedule = generateHunterSchedule(seed);

    this.engine = new Engine(canvas, { onFrame: (dt) => this.frame(dt) });
    this.engine.scene.background = new THREE.Color(0x06080e);
    this.engine.scene.fog = new THREE.Fog(0x06080e, 30, 80);
    this.engine.scene.add(new THREE.AmbientLight(0x506080, 0.5));
    const overhead = new THREE.DirectionalLight(0xfff0d0, 0.9);
    overhead.position.set(8, 24, 8);
    this.engine.scene.add(overhead);

    // Top-down camera. We pull it slightly toward +Z so the maw has
    // forward-look toward the bottom of the frame.
    this.engine.camera.position.set(0, 32, 0);
    this.engine.camera.lookAt(0, 0, 0);
    this.engine.camera.fov = 55;
    this.engine.camera.near = 0.1;
    this.engine.camera.far = 80;
    this.engine.camera.updateProjectionMatrix();

    // Arena floor — gridded wireframe at y=0.
    const floorGeo = new THREE.PlaneGeometry(ARENA_HALF * 2, ARENA_HALF * 2, 12, 12);
    floorGeo.rotateX(-Math.PI / 2);
    const floorMat = new THREE.MeshStandardMaterial({
      color: 0x0a0e1a, roughness: 0.9, metalness: 0.05,
      wireframe: false,
    });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    this.engine.scene.add(floor);
    const grid = new THREE.GridHelper(ARENA_HALF * 2, 24, 0x2a3050, 0x141828);
    grid.position.y = 0.01;
    this.engine.scene.add(grid);

    // Cityscape — one cylinder per object, scaled by mass.
    const cylGeo = new THREE.CylinderGeometry(0.5, 0.5, 1, 12);
    // v6 — every 12th object (deterministic by index) is a "neon"
    // cosmetic variant: brighter emissive, distinct hue. Mass is
    // unchanged so the scorer is unaffected. Per memory
    // feedback-sibling-games-shallow §"at least one surprise event"
    // — Maw had no shipped Phase 2; this fills the gap with a purely
    // visual surprise that reads instantly from across the arena.
    const NEON_HUES = [0.78, 0.55, 0.92, 0.18]; // purple, cyan, pink, gold
    for (let i = 0; i < this.objects.length; i++) {
      const obj = this.objects[i];
      const isNeon = i % 12 === 5; // seed-independent — same indices across runs
      let c: THREE.Color;
      let emissiveIntensity: number;
      if (isNeon) {
        c = new THREE.Color().setHSL(NEON_HUES[i % NEON_HUES.length], 0.95, 0.65);
        emissiveIntensity = 1.4;
      } else {
        // Mass scales the diameter (visual proxy). Color biased orange→cyan
        // for big→small so the player can read mass at a glance.
        const tint = THREE.MathUtils.lerp(0.0, 1.0, (obj.mass - MIN_MASS) / (MAX_MASS - MIN_MASS));
        c = new THREE.Color().setHSL(0.05 + 0.45 * (1 - tint), 0.8, 0.55);
        emissiveIntensity = 0.35;
      }
      const mat = new THREE.MeshStandardMaterial({
        color: c, emissive: c, emissiveIntensity,
        roughness: 0.5, metalness: 0.3,
      });
      const m = new THREE.Mesh(cylGeo, mat);
      m.position.set(obj.x, 0.5, obj.y);
      m.scale.set(obj.mass, Math.max(0.3, obj.mass * 0.6), obj.mass);
      this.engine.scene.add(m);
      this.objectMeshes.push(m);
    }

    // The maw itself — a ring + inner disc.
    const mawGeo = new THREE.RingGeometry(0.5, 0.7, 24);
    mawGeo.rotateX(-Math.PI / 2);
    const mawMat = new THREE.MeshStandardMaterial({
      color: 0xff5e3a, emissive: 0xff80a0, emissiveIntensity: 0.9,
      roughness: 0.4, metalness: 0.2, side: THREE.DoubleSide,
    });
    this.mawMesh = new THREE.Mesh(mawGeo, mawMat);
    this.mawMesh.position.set(0, 0.05, 0);
    this.engine.scene.add(this.mawMesh);
    this.mawGlow = new THREE.PointLight(0xff80a0, 1.0, 8, 1.6);
    this.mawGlow.position.set(0, 1.0, 0);
    this.engine.scene.add(this.mawGlow);

    // v5: hunter visual — small dark spheres with hostile red emissive.
    this.hunterGeo = new THREE.SphereGeometry(HUNTER_RADIUS, 12, 8);
    this.hunterMat = new THREE.MeshStandardMaterial({
      color: 0x180004, emissive: 0xff2030, emissiveIntensity: 1.2,
      roughness: 0.5, metalness: 0.2,
    });

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  }

  private frame(dt: number) {
    if (this.gameOver) return;
    const now = performance.now();
    const runMs = now - this.startedAt;
    if (runMs >= RUN_MS) { this.endRun(runMs); return; }
    // Boost intensity = how far past start radius you've grown, capped.
    // Bigger maw → more scene drama (depth-bar post-processing).
    const growth = (this.radius - MAW_START_RADIUS) / 6;
    const growthNorm = Math.max(0, Math.min(1, growth));
    this.engine.setBoost(growthNorm, dt);

    // v6 — adaptive audio drone tracks the same growth ratio so the
    // world deepens audibly as you grow. Per memory
    // feedback-sibling-games-shallow §"at least one escalation hook".
    // Capped at 0.7. AudioBed.setDroneIntensity damps internally so
    // per-frame setting is cheap.
    try { this.audio.setDroneIntensity(0.15 + growthNorm * 0.55); } catch { /* off */ }

    // Input → velocity. Touch overrides keyboard when non-zero (continuous
    // joystick magnitudes pass through to the accel integrator as scaled
    // ix/iy in [-1, 1] instead of the keyboard's discrete ±1).
    const kIx = (this.right ? 1 : 0) - (this.left ? 1 : 0);
    const kIy = (this.down ? 1 : 0) - (this.up ? 1 : 0);
    const ix = (this.touchVec.x !== 0 || this.touchVec.y !== 0) ? this.touchVec.x : kIx;
    const iy = (this.touchVec.x !== 0 || this.touchVec.y !== 0) ? this.touchVec.y : kIy;
    if (ix !== 0) this.mvx += ix * STEER_ACCEL * dt;
    else this.mvx -= this.mvx * Math.min(1, STEER_DAMP * dt);
    if (iy !== 0) this.mvy += iy * STEER_ACCEL * dt;
    else this.mvy -= this.mvy * Math.min(1, STEER_DAMP * dt);
    const speed = Math.hypot(this.mvx, this.mvy);
    if (speed > MAX_SPEED) {
      this.mvx = (this.mvx / speed) * MAX_SPEED;
      this.mvy = (this.mvy / speed) * MAX_SPEED;
    }
    this.mx += this.mvx * dt;
    this.my += this.mvy * dt;
    if (this.mx > ARENA_HALF) { this.mx = ARENA_HALF; this.mvx = 0; }
    if (this.mx < -ARENA_HALF) { this.mx = -ARENA_HALF; this.mvx = 0; }
    if (this.my > ARENA_HALF) { this.my = ARENA_HALF; this.mvy = 0; }
    if (this.my < -ARENA_HALF) { this.my = -ARENA_HALF; this.mvy = 0; }

    // Decay radius. Instantaneous decay-rate-at-now for HUD; the
    // server's closed-form integral is the authoritative version.
    const decayNow = DECAY_RATE + (DECAY_RATE_END - DECAY_RATE) * (runMs / RUN_MS);
    this.radius *= Math.exp(-decayNow * dt);

    // Consume any in-radius, eatable objects.
    const r = this.radius;
    for (let i = 0; i < this.objects.length; i++) {
      if (!this.alive[i]) continue;
      const obj = this.objects[i];
      const d = Math.hypot(this.mx - obj.x, this.my - obj.y);
      if (d <= r && obj.mass <= r) {
        this.alive[i] = 0;
        this.objectMeshes[i].visible = false;
        this.radius = Math.sqrt(this.radius * this.radius + obj.mass * obj.mass);
        if (this.radius > this.peakRadius) this.peakRadius = this.radius;
        if (this.eats.length < MAX_EATS) {
          this.eats.push({ t: Math.round(runMs), object_idx: i });
        }
        try { this.audio.chime(this.radius * 20); } catch { /* off */ }
      }
    }

    // v5: spawn scheduled hunters.
    while (this.hunterSpawnIdx < this.hunterSchedule.length &&
           this.hunterSchedule[this.hunterSpawnIdx].spawn_t <= runMs) {
      const hs = this.hunterSchedule[this.hunterSpawnIdx];
      const m = new THREE.Mesh(this.hunterGeo, this.hunterMat);
      m.position.set(hs.x, 0.5, hs.y);
      this.engine.scene.add(m);
      this.hunters.push({ idx: hs.idx, x: hs.x, y: hs.y, alive: true, mesh: m });
      this.hunterSpawnIdx += 1;
    }

    // v5: advance hunters toward maw, check bites.
    for (const h of this.hunters) {
      if (!h.alive) continue;
      const dx = this.mx - h.x;
      const dy = this.my - h.y;
      const d = Math.hypot(dx, dy);
      if (d > 0) {
        const step = Math.min(d, HUNTER_SPEED * dt);
        h.x += (dx / d) * step;
        h.y += (dy / d) * step;
      }
      h.mesh.position.set(h.x, 0.5, h.y);
      const dAfter = Math.hypot(this.mx - h.x, this.my - h.y);
      if (dAfter < this.radius + HUNTER_RADIUS) {
        h.alive = false;
        h.mesh.visible = false;
        this.radius *= HUNTER_BITE_FACTOR;
        this.hunterBites += 1;
        try { this.audio.thud(); } catch { /* off */ }
      }
    }

    // Update visuals.
    this.mawMesh.position.set(this.mx, 0.05, this.my);
    this.mawMesh.scale.set(this.radius, 1, this.radius);
    this.mawGlow.position.set(this.mx, 1.0, this.my);
    this.mawGlow.intensity = 0.8 + this.radius * 0.4;

    // Sample.
    this.sampleTimer += dt * 1000;
    if (this.sampleTimer >= SAMPLE_INTERVAL_MS) {
      this.sampleTimer = 0;
      if (this.samples.length < MAX_SAMPLES) {
        this.samples.push({ t: Math.round(runMs), x: q01(this.mx), y: q01(this.my) });
      }
    }

    const score = Math.floor(this.peakRadius * 100) + this.eats.length;
    this.cb.onHud(runMs, this.radius, this.eats.length, score);
  }

  private endRun(runMs: number) {
    this.gameOver = true;
    // v6 — quiet the adaptive drone so it doesn't bleed into game-over.
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    if (this.samples.length === 0 || this.samples[this.samples.length - 1].t < Math.round(runMs)) {
      if (this.samples.length < MAX_SAMPLES) {
        this.samples.push({ t: Math.round(runMs), x: q01(this.mx), y: q01(this.my) });
      }
    }
    const result: MawResult = {
      peak_radius: Math.round(this.peakRadius * 100) / 100,
      eats: this.eats.length,
      hunter_bites: this.hunterBites,
      duration_ms: Math.round(runMs),
      samples: this.samples,
      eatEvents: this.eats,
      scoreEstimate: Math.floor(this.peakRadius * 100) + this.eats.length,
    };
    this.cb.onGameOver(result);
  }

  start() {
    this.startedAt = performance.now();
    this.samples.push({ t: 0, x: 0, y: 0 });
    this.engine.start();
  }
  armAudio() { try { this.audio.arm(); } catch { /* gesture-gated */ } }
  setVolume(v: number) { this.audio.setVolume(v); }
  setMuted(m: boolean) { this.audio.setMuted(m); }
  dispose() {
    this.gameOver = true;
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    this.engine.stop();
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
  }
}
